import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Box, Camera, ChevronRight, Clock, MapPin, Plus, Search, Sparkles, TrendingUp } from "lucide-react";
import AppShell from "../components/AppShell";
import ExportMenu from "../components/ExportMenu";
import { api, API_URL } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ProductPhoto({ product }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!product.has_photo) return;
    const token = window.localStorage.getItem("session_token");
    fetch(`${API_URL}/inventory/products/${product.id}/photo`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => b && setUrl(URL.createObjectURL(b)))
      .catch(() => setUrl(""));
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.has_photo, product.id]);
  return url ? <img src={url} alt={product.name} /> : <Box size={28} strokeWidth={1.5} />;
}

function emptyForm() {
  return { name: "", category: "", description: "", cost: "", selling_price: "", low_stock_threshold: 5, unit: "pz", location: "", stock_quantity: 0 };
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [showLow, setShowLow] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (showLow) params.set("low_stock", "true");
    try {
      const [{ data: list }, { data: dash }] = await Promise.all([
        api.get(`/inventory/products?${params.toString()}`, { withCredentials: true }),
        api.get(`/inventory/dashboard`, { withCredentials: true }),
      ]);
      setProducts(list || []);
      setDashboard(dash);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar el inventario.");
    }
  }, [search, category, showLow]);

  useEffect(() => { load(); }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await api.post("/inventory/products", form, { withCredentials: true });
      setShowForm(false);
      setForm(emptyForm());
      navigate(`/inventario/${response.data.id}`);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos crear el producto.");
    } finally {
      setSaving(false);
    }
  };

  const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));

  return (
    <AppShell>
      <div className="page inventory-page">
        <section className="inv-hero">
          <div>
            <small className="eyebrow">INVENTARIO</small>
            <h1>Lo que tienes, <em>a la mano.</em></h1>
            <p>Registra productos, variaciones, ubicaciones y movimientos. Simple, sin ERP.</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <ExportMenu resource="inventory" testid="inventory-export" label="Exportar catálogo" snapshot />
            <button type="button" className="cash-btn primary" onClick={() => setShowForm(true)} data-testid="inventory-add-product-button">
              <Plus size={16} /> Nuevo producto
            </button>
          </div>
        </section>

        {dashboard && (
          <section className="inv-kpis" data-testid="inventory-kpis">
            <article className="inv-kpi inv-kpi-primary">
              <small>Total productos</small>
              <b>{dashboard.total_products}</b>
              <span>{dashboard.total_units} unidades en total</span>
            </article>
            <article className="inv-kpi inv-kpi-value">
              <small>Valor total</small>
              <b>{money(dashboard.total_value)}</b>
              <span>Costo × existencias</span>
            </article>
            <article className={`inv-kpi ${dashboard.low_stock.length ? "inv-kpi-warn" : "inv-kpi-ok"}`} onClick={() => setShowLow((v) => !v)} data-testid="inventory-kpi-low-stock">
              <small>Stock bajo</small>
              <b>{dashboard.low_stock.length}</b>
              <span>{dashboard.low_stock.length ? "Toca para filtrar" : "Todo en orden"}</span>
            </article>
            <article className="inv-kpi inv-kpi-idle">
              <small>Sin movimiento (60d)</small>
              <b>{dashboard.idle_products.length}</b>
              <span>Considera promoción o devolución</span>
            </article>
          </section>
        )}

        {dashboard && (dashboard.by_location.length > 0 || dashboard.by_category.length > 0) && (
          <section className="inv-breakdown" data-testid="inventory-breakdown">
            {dashboard.by_location.length > 0 && (
              <article>
                <header><MapPin size={15} /> Por ubicación</header>
                <ul>
                  {dashboard.by_location.slice(0, 6).map((row) => (
                    <li key={row.location}><span>{row.location}</span><b>{row.quantity} un.</b></li>
                  ))}
                </ul>
              </article>
            )}
            {dashboard.by_category.length > 0 && (
              <article>
                <header><Sparkles size={15} /> Valor por categoría</header>
                <ul>
                  {dashboard.by_category.slice(0, 6).map((row) => (
                    <li key={row.category}><span>{row.category}</span><b>{money(row.value)}</b></li>
                  ))}
                </ul>
              </article>
            )}
          </section>
        )}

        <section className="inv-filters">
          <label className="cash-search">
            <Search size={14} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto, categoría, descripción..." data-testid="inventory-search" />
          </label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="inventory-filter-category">
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          <button type="button" className={showLow ? "active" : ""} onClick={() => setShowLow(!showLow)} data-testid="inventory-filter-low">
            <AlertTriangle size={13} /> Solo stock bajo
          </button>
        </section>

        {error && <p className="profile-error" data-testid="inventory-error">{error}</p>}

        <section className="inv-grid" data-testid="inventory-grid">
          {products.length === 0 && (
            <div className="inv-empty">
              <Box size={48} strokeWidth={1.2} />
              <b>Aún no tienes productos</b>
              <span>Crea tu primer producto y empieza a llevar tu inventario.</span>
              <button type="button" className="cash-btn primary" onClick={() => setShowForm(true)}><Plus size={14} /> Crear producto</button>
            </div>
          )}
          {products.map((p) => (
            <Link key={p.id} to={`/inventario/${p.id}`} className="inv-card" data-testid={`inventory-product-${p.id}`}>
              <div className="inv-card-photo"><ProductPhoto product={p} /></div>
              <div className="inv-card-body">
                <header>
                  <b>{p.name}</b>
                  {p.is_low_stock && <span className="inv-badge warn"><AlertTriangle size={11} /> Bajo</span>}
                </header>
                <small>{p.category || "Sin categoría"}</small>
                <div className="inv-card-stats">
                  <span><b>{p.total_quantity}</b> {p.unit || "un."}</span>
                  <span>{money(p.selling_price)}</span>
                </div>
                <footer>
                  <TrendingUp size={12} /> Margen {p.margin}% · <span>{money(p.inventory_value)} en stock</span>
                  <ChevronRight size={14} />
                </footer>
              </div>
            </Link>
          ))}
        </section>

        {showForm && (
          <div className="cash-modal-backdrop" onClick={() => setShowForm(false)}>
            <div className="cash-modal" onClick={(e) => e.stopPropagation()} data-testid="inventory-new-modal">
              <header>
                <div>
                  <small>NUEVO PRODUCTO</small>
                  <b>Agrega un artículo</b>
                </div>
                <button type="button" onClick={() => setShowForm(false)} data-testid="inventory-new-close">✕</button>
              </header>
              <form onSubmit={save}>
                <div className="cash-modal-grid">
                  <label className="full">Nombre<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="inventory-form-name" /></label>
                  <label>Categoría<input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ropa, Comida..." data-testid="inventory-form-category" /></label>
                  <label>Unidad<input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pz, kg, m..." data-testid="inventory-form-unit" /></label>
                  <label>Costo unitario<input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="inventory-form-cost" /></label>
                  <label>Precio de venta<input type="number" step="0.01" min="0" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="inventory-form-price" /></label>
                  <label>Existencias iniciales<input type="number" step="1" min="0" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} data-testid="inventory-form-stock" /></label>
                  <label>Alerta stock bajo<input type="number" step="1" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} data-testid="inventory-form-threshold" /></label>
                  <label className="full">Ubicación<input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bodega A, Repisa 3..." data-testid="inventory-form-location" /></label>
                  <label className="full">Descripción<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="inventory-form-description" /></label>
                </div>
                {error && <p className="profile-error">{error}</p>}
                <div className="cash-modal-actions">
                  <button type="button" onClick={() => setShowForm(false)} className="cash-btn ghost">Cancelar</button>
                  <button type="submit" className="cash-btn primary" disabled={saving} data-testid="inventory-form-save">
                    {saving ? "Guardando..." : "Crear producto"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
